/**
 * useRosterHorizon — the planning-window control state.
 *
 * Replaces the hardcoded 7-day window: the lead picks Woche (7) / 2 Wochen (14)
 * / Monat (28), and the choice is persisted so the next session opens on the
 * same rhythm. The window feeds BOTH the board's columns (`days`) and the shift
 * read (`{from, to}`). Date-range nav steps by the selected horizon.
 */
import { useCallback, useMemo, useState } from 'react'

import { STORAGE_KEY_CONFIG } from '@/lib/constants'
import {
  type Horizon,
  isHorizon,
  todayIso,
  addDays,
  windowDays,
} from './rosterModel'

const HORIZON_STORAGE_KEY = `${STORAGE_KEY_CONFIG}:roster-horizon`

function loadHorizon(horizons: readonly Horizon[]): Horizon {
  const fallback = horizons[0] ?? 7
  if (typeof window === 'undefined') return fallback
  const raw = window.localStorage.getItem(HORIZON_STORAGE_KEY)
  const parsed = raw ? Number(raw) : NaN
  return isHorizon(parsed, horizons) ? parsed : fallback
}

export interface RosterHorizon {
  horizon: Horizon
  /** The selectable horizons (config-resolved) the control offers. */
  horizons: readonly Horizon[]
  /** ISO first day of the window. */
  anchor: string
  /** All ISO days in the window (the board's columns). */
  days: string[]
  /** First / last ISO day (the shift read's `{from, to}`). */
  from: string
  to: string
  setHorizon: (h: Horizon) => void
  /** Step the window backward / forward by one horizon length. */
  prev: () => void
  next: () => void
}

export function useRosterHorizon(horizons: readonly Horizon[]): RosterHorizon {
  const [horizon, setHorizonState] = useState<Horizon>(() => loadHorizon(horizons))
  const [anchor, setAnchor] = useState<string>(todayIso)

  const setHorizon = useCallback((h: Horizon) => {
    setHorizonState(h)
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(HORIZON_STORAGE_KEY, String(h))
    }
  }, [])

  const prev = useCallback(() => setAnchor((a) => addDays(a, -horizon)), [horizon])
  const next = useCallback(() => setAnchor((a) => addDays(a, horizon)), [horizon])

  const days = useMemo(() => windowDays(anchor, horizon), [anchor, horizon])

  return {
    horizon,
    horizons,
    anchor,
    days,
    from: days[0] ?? anchor,
    to: days[days.length - 1] ?? anchor,
    setHorizon,
    prev,
    next,
  }
}
